class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Watch the primary cart icon for ANY change and mirror it to all other headers
    this.observeCartIcon();
  }

  /**
   * MutationObserver on #cart-icon-bubble.
   * Fires whenever Shopify updates it — whether from add-to-cart,
   * quantity change, or item removal inside the drawer.
   */
  observeCartIcon() {
    const primaryIcon = document.getElementById('cart-icon-bubble');
    if (!primaryIcon) return;

    // Disconnect any existing observer before creating a new one
    if (this._cartIconObserver) {
      this._cartIconObserver.disconnect();
    }

    this._cartIconObserver = new MutationObserver(() => {
      this.mirrorCartIconToAllHeaders(primaryIcon);
    });

    this._cartIconObserver.observe(primaryIcon, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: false,
    });
  }

  /**
   * Copies the current state of the primary cart icon
   * (SVG + count bubble) into every other header's cart icon.
   */
  mirrorCartIconToAllHeaders(primaryIcon) {
    const otherIcons = document.querySelectorAll('.cart-icon-bubble-all:not(#cart-icon-bubble)');

    otherIcons.forEach((icon) => {
      // --- Sync the cart SVG (empty vs filled) ---
      const primarySvgWrapper = primaryIcon.querySelector('.svg-wrapper');
      const targetSvgWrapper = icon.querySelector('.svg-wrapper');
      if (primarySvgWrapper && targetSvgWrapper) {
        targetSvgWrapper.innerHTML = primarySvgWrapper.innerHTML;
      }

      // --- Sync the count bubble ---
      const primaryBubble = primaryIcon.querySelector('.cart-count-bubble');
      const existingBubble = icon.querySelector('.cart-count-bubble');

      if (primaryBubble) {
        const clonedBubble = primaryBubble.cloneNode(true);
        if (existingBubble) {
          icon.replaceChild(clonedBubble, existingBubble);
        } else {
          icon.appendChild(clonedBubble);
        }
      } else {
        if (existingBubble) {
          icon.removeChild(existingBubble);
        }
      }
    });
  }

  setHeaderCartIconAccessibility() {
    // Target ALL cart icon bubbles across every header on the page
    const cartLinks = document.querySelectorAll('.cart-icon-bubble-all');
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      // Avoid adding duplicate listeners on re-calls
      if (cartLink.dataset.drawerBound) return;
      cartLink.dataset.drawerBound = 'true';

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.observeCartIcon();

    setTimeout(() => {
      const primaryIcon = document.getElementById('cart-icon-bubble');
      if (primaryIcon) this.mirrorCartIconToAllHeaders(primaryIcon);
    }, 50);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
